/**
 * Client for the Wayfarer auth/sync backend (server/ in this repo, mounted at
 * <base>/api).
 *
 * Sessions are cookie-based: the browser carries the httpOnly session cookie
 * and that remains the primary carrier. A copy of the token is ALSO kept in
 * localStorage and sent as `X-Wf-Session` when the cookie is missing.
 *
 * That is a deliberate reversal of this file's original rule ("nothing
 * token-like in JS-visible storage"), made because the rule cost more than it
 * bought here: car head units wipe cookies on exit and give the installed PWA
 * a different cookie jar from the browser tab, so the driver signed in on
 * every trip despite sessions being long-lived. The cost is that an XSS on
 * this origin can read the token rather than only ride the session - accepted
 * because the app renders no third-party content and no user-authored HTML.
 * If that ever changes, this decision needs revisiting.
 */

import type { SpotifyPin, UserSettings } from '../core/settings';
import type { LngLat } from '../core/types';

export interface User {
  id: number;
  name: string;
  role: 'admin' | 'driver';
  status: 'pending' | 'approved' | 'disabled';
}

export interface PendingUser {
  id: number;
  name: string;
  createdAt: string;
  status: User['status'];
  role: User['role'];
}

const API = `${import.meta.env.BASE_URL}api`;

const LS_SESSION = 'wayfarer.session';
const LS_LAST_NAME = 'wayfarer.lastName';
const LS_APPROVED_USER = 'wayfarer.approvedUser';

function storedToken(): string | null {
  try {
    return localStorage.getItem(LS_SESSION);
  } catch {
    return null; // storage disabled - the cookie alone still works
  }
}

/**
 * The last name that signed in successfully, for prefilling the login form.
 *
 * Only the name, never the PIN: a remembered PIN is a credential sitting in
 * storage on a head unit that several drivers can reach, whereas a remembered
 * name is the same information anyone can read off the account list. This is
 * kept ACROSS logout on purpose - the driver signing out is nearly always the
 * same one who signs back in, and clearing it just makes them type it again.
 */
export function lastName(): string {
  try {
    return localStorage.getItem(LS_LAST_NAME) ?? '';
  } catch {
    return '';
  }
}

function storeLastName(name: string): void {
  try {
    localStorage.setItem(LS_LAST_NAME, name);
  } catch {
    /* non-fatal: the driver just types their name next time */
  }
}

function storeToken(token: string | null | undefined): void {
  try {
    if (token) localStorage.setItem(LS_SESSION, token);
    else localStorage.removeItem(LS_SESSION);
  } catch {
    /* non-fatal: we just lose the cookie-wipe fallback */
  }
}

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    const token = storedToken();
    res = await fetch(`${API}${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // Sent on every call, not only when the cookie looks absent: the
        // cookie is httpOnly, so JS cannot tell whether it survived. The
        // server prefers the cookie and only falls back to this.
        ...(token ? { 'X-Wf-Session': token } : {}),
        ...init.headers,
      },
      // A hung request must fail visibly, never stall the UI forever - flaky
      // car connectivity and broken HTTP/3 paths both present as hangs.
      signal: init.signal ?? AbortSignal.timeout(12_000),
    });
  } catch (err) {
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      throw new ApiError('Server not responding - check your connection and try again.', 0);
    }
    throw new ApiError('Network error - are you online?', 0);
  }
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* empty body is fine for some endpoints */
  }
  if (!res.ok) {
    // A rejected session is dead for good - drop the stored copy rather than
    // re-sending it on every call for the rest of the install's life.
    if (res.status === 401) {
      storeToken(null);
      storeApprovedUser(null);
    }
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return body as T;
}

/* ------------------------------ session ---------------------------------- */

export async function me(signal?: AbortSignal): Promise<{ user: User | null }> {
  try {
    const result = await call<{ user: User | null }>('/me', signal ? { signal } : {});
    storeApprovedUser(result.user);
    return result;
  } catch (err) {
    // A previously approved install can boot through a coverage gap. The
    // server remains authoritative whenever it is reachable.
    if (err instanceof ApiError && err.status === 0 && storedToken()) {
      const cached = storedApprovedUser();
      if (cached) return { user: cached };
    }
    throw err;
  }
}

export async function signup(name: string, pin: string): Promise<{ user: User }> {
  const r = await call<{ user: User; token?: string | null }>('/signup', {
    method: 'POST',
    body: JSON.stringify({ name, pin }),
  });
  storeToken(r.token);
  // Signup succeeded even though no session exists yet (pending approval), so
  // the name is worth keeping - it saves retyping it on the login that follows.
  storeLastName(r.user.name);
  storeApprovedUser(r.user);
  return r;
}

export async function login(name: string, pin: string): Promise<{ user: User }> {
  const r = await call<{ user: User; token?: string | null }>('/login', {
    method: 'POST',
    body: JSON.stringify({ name, pin }),
  });
  storeToken(r.token);
  // The server's spelling, not what was typed: name matching is
  // case-insensitive, so this also corrects the prefill's capitalisation.
  storeLastName(r.user.name);
  storeApprovedUser(r.user);
  return r;
}

export async function logout(): Promise<void> {
  try {
    await call('/logout', { method: 'POST' });
  } finally {
    // Clear locally even if the server call failed, or a driver who signs out
    // with no connection stays signed in on the next launch.
    storeToken(null);
    storeApprovedUser(null);
  }
}

function storedApprovedUser(): User | null {
  try {
    const raw = localStorage.getItem(LS_APPROVED_USER);
    if (!raw) return null;
    const user = JSON.parse(raw) as Partial<User>;
    if (
      !Number.isInteger(user.id) ||
      typeof user.name !== 'string' ||
      (user.role !== 'admin' && user.role !== 'driver') ||
      user.status !== 'approved'
    ) {
      localStorage.removeItem(LS_APPROVED_USER);
      return null;
    }
    return user as User;
  } catch {
    return null;
  }
}

function storeApprovedUser(user: User | null | undefined): void {
  try {
    if (user?.status === 'approved') localStorage.setItem(LS_APPROVED_USER, JSON.stringify(user));
    else localStorage.removeItem(LS_APPROVED_USER);
  } catch {
    /* storage disabled - online cookie auth still works */
  }
}

/* -------------------------- user settings ------------------------------ */

export interface UserSettingsRecord {
  values: UserSettings | null;
  revision: number;
  updatedAt: string | null;
}

export function fetchUserSettings(
  userId: number,
  signal?: AbortSignal,
): Promise<{ settings: UserSettingsRecord }> {
  return call(`/settings?userId=${encodeURIComponent(userId)}`, signal ? { signal } : {});
}

export function saveUserSettings(
  userId: number,
  settings: UserSettings,
): Promise<{ settings: UserSettingsRecord }> {
  return call('/settings', {
    method: 'PUT',
    body: JSON.stringify({ userId, settings }),
  });
}

/* ------------------------- per-user breadcrumbs ------------------------- */

export interface BreadcrumbRecord {
  runs: LngLat[][];
  revision: number;
  updatedAt: string | null;
}

export interface BreadcrumbBatch {
  id: string;
  points: LngLat[];
  /** Append to the last run; false starts a new proven stretch of road. */
  continueLast: boolean;
}

/** Ownership is derived from the authenticated session on the server. */
export function fetchBreadcrumbs(): Promise<{ breadcrumbs: BreadcrumbRecord }> {
  return call('/breadcrumbs');
}

/** Stable batch ids make retries safe after a timeout or connectivity loss. */
export function appendBreadcrumbBatch(
  batch: BreadcrumbBatch,
): Promise<{ breadcrumbs: Omit<BreadcrumbRecord, 'runs'> }> {
  return call('/breadcrumbs', {
    method: 'POST',
    body: JSON.stringify(batch),
  });
}

/* ------------------------ Spotify preferences --------------------------- */

export interface SpotifyPreferences {
  spotifyAccountId: string | null;
  pins: SpotifyPin[];
  revision: number;
  updatedAt: string | null;
}

export function fetchSpotifyPreferences(userId: number): Promise<{ preferences: SpotifyPreferences }> {
  return call(`/spotify/preferences?userId=${encodeURIComponent(userId)}`);
}

export function saveSpotifyPreferences(
  userId: number,
  pins: SpotifyPin[],
  spotifyAccountId: string | null,
): Promise<{ preferences: SpotifyPreferences }> {
  return call('/spotify/preferences', {
    method: 'PUT',
    body: JSON.stringify({ userId, pins, spotifyAccountId }),
  });
}

/* ------------------------------- admin ------------------------------------ */

export function adminListUsers(): Promise<{ users: PendingUser[] }> {
  return call('/admin/users');
}

export function adminSetStatus(
  userId: number,
  status: 'approved' | 'disabled',
): Promise<{ ok: true }> {
  return call(`/admin/users/${userId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export function adminResetPin(userId: number, pin: string): Promise<{ ok: true }> {
  return call(`/admin/users/${userId}/pin`, { method: 'POST', body: JSON.stringify({ pin }) });
}

/* ------------------------------- trips ------------------------------------ */

import type { Place, Trip } from '../core/types';

/** Push a finished trip to the server (also kept in local IndexedDB). */
export function syncTrip(trip: Trip): Promise<{ ok: true }> {
  return call('/trips', { method: 'POST', body: JSON.stringify(trip) });
}

export function fetchTrips(): Promise<{ trips: Trip[] }> {
  return call('/trips');
}

/* ------------------------- user route synchronisation -------------------- */

export type UserRouteStatus = 'planned' | 'navigating' | 'arrived' | 'ended';

/**
 * A destination plan shared only between devices signed in as the same user.
 *
 * The server derives ownership from the authenticated session. There is no
 * user id in this shape deliberately: accepting one from the client would let
 * a device select somebody else's route collection.
 */
export interface UserRoute {
  id: string;
  destination: Place;
  /** Ordered intermediate destinations before the final destination. */
  stops?: Place[];
  status: UserRouteStatus;
  sourceDevice: string;
  createdAt: number;
  updatedAt: number;
}

export function saveUserRoute(
  route: Pick<UserRoute, 'id' | 'destination' | 'stops' | 'status' | 'sourceDevice'>,
): Promise<{ route: UserRoute }> {
  return call('/route-plans', { method: 'POST', body: JSON.stringify(route) });
}

export function fetchUserRoutes(activeOnly = false): Promise<{
  active: UserRoute | null;
  routes: UserRoute[];
}> {
  return call(`/route-plans${activeOnly ? '?active=1' : ''}`);
}

/* --------------------------- shared saved places -------------------------- */

export type PlaceCategory = 'home' | 'work' | 'showroom' | 'depot' | 'other';

export interface SavedPlace {
  id: string;
  name: string;
  lng: number;
  lat: number;
  kind: 'place' | 'shared-location';
  category?: PlaceCategory;
  /**
   * Which folder it is filed under, or null/absent for unfiled.
   *
   * A free-form name rather than an id: folders are a filing label the fleet
   * agrees on ("Sauna customers", "Shops"), and a place whose folder no longer
   * exists reads as unfiled rather than disappearing into a dangling reference.
   */
  folder?: string | null;
  createdBy: number;
  creatorName: string;
  createdAt: string;
}

export function fetchPlaces(): Promise<{ places: SavedPlace[] }> {
  return call('/places');
}

export function createPlace(
  name: string,
  lng: number,
  lat: number,
  kind: 'place' | 'shared-location' = 'place',
  category: PlaceCategory = 'other',
  /** File it straight away; the server creates the folder if it is new. */
  folder: string | null = null,
): Promise<{ place: SavedPlace }> {
  return call('/places', {
    method: 'POST',
    body: JSON.stringify({ name, lng, lat, kind, category, folder }),
  });
}

export function deletePlace(id: string): Promise<{ ok: true }> {
  return call(`/places/${id}`, { method: 'DELETE' });
}

/** Rename a saved place; creator or admin only, enforced server-side. */
export function renamePlace(id: string, name: string): Promise<{ ok: true }> {
  return call(`/places/${id}/name`, { method: 'POST', body: JSON.stringify({ name }) });
}

/* --------------------------- folders for places ---------------------------- */

/** Every folder the fleet has, including ones with nothing in them yet. */
export function fetchFolders(): Promise<{ folders: string[] }> {
  return call('/folders');
}

export function createFolder(name: string): Promise<{ ok: true; existed: boolean }> {
  return call('/folders', { method: 'POST', body: JSON.stringify({ name }) });
}

/** Removes the folder only - the places inside it become unfiled, never deleted. */
export function deleteFolder(name: string): Promise<{ ok: true; unfiled: number }> {
  return call(`/folders/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/** File a place, or pass null to take it out of its folder. */
export function setPlaceFolder(id: string, folder: string | null): Promise<{ ok: true }> {
  return call(`/places/${id}/folder`, { method: 'POST', body: JSON.stringify({ folder }) });
}

/* ------------------------------- AI search -------------------------------- */

export interface AiSuggestion {
  /** What to feed the geocoder. */
  query: string;
  /** The name to show the driver. */
  label: string;
  /** One line on why it matches what they asked. */
  why: string;
}

/**
 * Ask the model to turn a loose description into searchable place names.
 *
 * Deliberately returns TERMS, not positions - the caller geocodes each one, so
 * every tappable result still comes from the map rather than the model.
 */
export function aiSearch(
  q: string,
  near: { lat: number; lng: number } | null,
  lang: string,
  signal?: AbortSignal,
): Promise<{ suggestions: AiSuggestion[] }> {
  return call('/ai/search', {
    method: 'POST',
    body: JSON.stringify({ q, lang, lat: near?.lat, lng: near?.lng }),
    signal,
  });
}

/* ------------------------------ push notifications ------------------------- */

/** The server's VAPID public key; 503 when push is unavailable there. */
export function fetchPushKey(): Promise<{ publicKey: string }> {
  return call('/push/key');
}

export function pushSubscribe(endpoint: string): Promise<{ ok: true }> {
  return call('/push/subscribe', { method: 'POST', body: JSON.stringify({ endpoint }) });
}

export function pushUnsubscribe(endpoint: string): Promise<{ ok: true }> {
  return call('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) });
}

export { ApiError };
